/**
 * Minimap component for PacMassive
 * Shows a simplified top-down view of the maze in the bottom right corner
 */

import { getGameState, getLocalPlayerId } from "./gameState";
import { VISUAL_CONFIG } from "@shared/config";

let minimapCanvas: HTMLCanvasElement | null = null;
let minimapContext: CanvasRenderingContext2D | null = null;
let isMinimapInitialized = false;

/**
 * Initialize the minimap component
 */
export function initializeMinimap(): void {
  if (isMinimapInitialized) return;

  // Create minimap container
  const minimapContainer = document.createElement("div");
  minimapContainer.id = "minimap-container";
  minimapContainer.style.position = "absolute";
  minimapContainer.style.bottom = "20px";
  minimapContainer.style.right = "20px";
  minimapContainer.style.width = "180px";
  minimapContainer.style.height = "180px";
  minimapContainer.style.backgroundColor = "rgba(0, 0, 0, 0.6)";
  minimapContainer.style.border = "2px solid rgba(0, 150, 255, 0.7)";
  minimapContainer.style.borderRadius = "5px";
  minimapContainer.style.zIndex = "100";
  minimapContainer.style.overflow = "hidden";
  minimapContainer.style.boxShadow = "0 0 10px rgba(0, 150, 255, 0.5)";

  // Create canvas for drawing the minimap
  minimapCanvas = document.createElement("canvas");
  minimapCanvas.id = "minimap-canvas";
  minimapCanvas.width = 180;
  minimapCanvas.height = 180;
  minimapContainer.appendChild(minimapCanvas);

  // Get the canvas context for drawing
  minimapContext = minimapCanvas.getContext("2d");
  if (!minimapContext) {
    console.error("Could not get 2D context for minimap canvas");
    return;
  }

  // Add the minimap to the document
  document.body.appendChild(minimapContainer);
  isMinimapInitialized = true;

  // Add a title to the minimap
  const minimapTitle = document.createElement("div");
  minimapTitle.textContent = "MINIMAP";
  minimapTitle.style.position = "absolute";
  minimapTitle.style.top = "4px";
  minimapTitle.style.left = "0";
  minimapTitle.style.width = "100%";
  minimapTitle.style.fontSize = "10px";
  minimapTitle.style.fontFamily = "Arial, sans-serif";
  minimapTitle.style.textAlign = "center";
  minimapTitle.style.color = "rgba(0, 150, 255, 1)";
  minimapTitle.style.fontWeight = "bold";
  minimapContainer.appendChild(minimapTitle);
}

/**
 * Update the minimap with the current game state
 */
export function updateMinimap(): void {
  if (!minimapCanvas || !minimapContext) return;

  const gameState = getGameState();
  if (!gameState) return;

  const { maze, players, ghosts, mazeSize } = gameState;
  if (!maze || maze.length === 0) return;

  // Clear the canvas
  minimapContext.clearRect(0, 0, minimapCanvas.width, minimapCanvas.height);

  // Calculate cell size based on maze dimensions
  const cellSize = Math.min(
    minimapCanvas.width / mazeSize,
    minimapCanvas.height / mazeSize
  );

  // Calculate offset to center the maze in the minimap
  const offsetX = (minimapCanvas.width - cellSize * mazeSize) / 2;
  const offsetY = (minimapCanvas.height - cellSize * mazeSize) / 2;

  // Draw background
  minimapContext.fillStyle = "rgba(0, 0, 30, 0.8)";
  minimapContext.fillRect(
    offsetX,
    offsetY,
    cellSize * mazeSize,
    cellSize * mazeSize
  );

  // Draw maze cells
  for (const cell of maze) {
    const x = offsetX + cell.x * cellSize;
    const y = offsetY + cell.y * cellSize;

    // Draw walls
    minimapContext.strokeStyle = "rgba(0, 150, 255, 0.8)";
    minimapContext.lineWidth = 1;

    // Draw north wall
    if (cell.walls[0]) {
      minimapContext.beginPath();
      minimapContext.moveTo(x, y);
      minimapContext.lineTo(x + cellSize, y);
      minimapContext.stroke();
    }

    // Draw east wall
    if (cell.walls[1]) {
      minimapContext.beginPath();
      minimapContext.moveTo(x + cellSize, y);
      minimapContext.lineTo(x + cellSize, y + cellSize);
      minimapContext.stroke();
    }

    // Draw south wall
    if (cell.walls[2]) {
      minimapContext.beginPath();
      minimapContext.moveTo(x, y + cellSize);
      minimapContext.lineTo(x + cellSize, y + cellSize);
      minimapContext.stroke();
    }

    // Draw west wall
    if (cell.walls[3]) {
      minimapContext.beginPath();
      minimapContext.moveTo(x, y);
      minimapContext.lineTo(x, y + cellSize);
      minimapContext.stroke();
    }

    // Draw dots (with smaller size)
    if (cell.hasDot) {
      minimapContext.fillStyle = "rgba(255, 255, 255, 0.7)";
      minimapContext.beginPath();
      minimapContext.arc(
        x + cellSize / 2,
        y + cellSize / 2,
        cellSize / 8,
        0,
        Math.PI * 2
      );
      minimapContext.fill();
    }

    // Draw rockets
    if (cell.hasRocket) {
      // Draw rocket with enhanced visibility (bright orange with glow effect)
      // Outer glow
      minimapContext.fillStyle = "rgba(255, 140, 0, 0.4)";
      minimapContext.beginPath();
      minimapContext.arc(
        x + cellSize / 2,
        y + cellSize / 2,
        cellSize / 3,
        0,
        Math.PI * 2
      );
      minimapContext.fill();

      // Bright inner circle
      minimapContext.fillStyle = "rgba(255, 100, 0, 1)";
      minimapContext.beginPath();
      minimapContext.arc(
        x + cellSize / 2,
        y + cellSize / 2,
        cellSize / 5,
        0,
        Math.PI * 2
      );
      minimapContext.fill();

      // Add rocket shape (small triangle)
      minimapContext.fillStyle = "rgba(255, 255, 0, 1)";
      minimapContext.beginPath();
      minimapContext.moveTo(x + cellSize / 2, y + cellSize / 2 - cellSize / 6);
      minimapContext.lineTo(
        x + cellSize / 2 - cellSize / 8,
        y + cellSize / 2 + cellSize / 8
      );
      minimapContext.lineTo(
        x + cellSize / 2 + cellSize / 8,
        y + cellSize / 2 + cellSize / 8
      );
      minimapContext.closePath();
      minimapContext.fill();
    }
  }

  // Get local player ID
  const localPlayerId = getLocalPlayerId();

  // Draw players
  for (const [playerId, player] of Object.entries(players)) {
    const x = offsetX + player.x * cellSize;
    const y = offsetY + player.y * cellSize;
    const isLocalPlayer = playerId === localPlayerId;

    // Use team color for players
    const teamColorHex =
      player.team === "A"
        ? VISUAL_CONFIG.COLORS.TEAM_A
        : VISUAL_CONFIG.COLORS.TEAM_B;

    // Convert hex color to rgba
    const r = (teamColorHex >> 16) & 255;
    const g = (teamColorHex >> 8) & 255;
    const b = teamColorHex & 255;

    // Draw player circle
    minimapContext.fillStyle = `rgba(${r}, ${g}, ${b}, ${
      isLocalPlayer ? 1 : 0.7
    })`;
    minimapContext.beginPath();
    minimapContext.arc(x, y, cellSize / 2.5, 0, Math.PI * 2);
    minimapContext.fill();

    // Add border for local player
    if (isLocalPlayer) {
      minimapContext.strokeStyle = "white";
      minimapContext.lineWidth = 2;
      minimapContext.stroke();
    }
  }

  // Draw ghosts
  for (const ghost of Object.values(ghosts)) {
    const x = offsetX + ghost.x * cellSize;
    const y = offsetY + ghost.y * cellSize;

    // Draw ghost (red with transparency)
    minimapContext.fillStyle = "rgba(255, 0, 0, 0.7)";
    minimapContext.beginPath();
    minimapContext.arc(x, y, cellSize / 3, 0, Math.PI * 2);
    minimapContext.fill();
  }
}
